import { useImmer } from "use-immer";
import { Line } from "react-chartjs-2";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import Loader from "../../Loader/Loader";
import { getBHMSHistoryConfig } from "./BhmsGraphConfig";
import { BHMS_STATION_CONFIG_MAP } from "../../bhms/bhms_config";
import {
  getAnnotaionsForPointGraph,
  getMaxValueOfSgYaxis,
  getTransformedSgHistorydata,
  retrieveTransformedValue,
} from "./BhmsGraphUtils";

const getOrCreateTooltip = (chart: any) => {
  let tooltipEl = chart.canvas.parentNode.querySelector("div");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.style.background = "rgba(0, 0, 0, 0.7)";
    tooltipEl.style.borderRadius = "3px";
    tooltipEl.style.color = "white";
    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.transform = "translate(-50%, 0)";
    tooltipEl.style.transition = "all .1s ease";
    tooltipEl.style.zIndex = 10000;
    tooltipEl.style.width = "max-content";

    const table = document.createElement("table");
    table.style.margin = "0px";

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const externalTooltipHandler = (context: any, keysVirtualThreshold: any, STATIC_KEYS_INFO: any) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set Text
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b: any) => b.lines);

    const tableHead = document.createElement("thead");

    titleLines.forEach((title: any) => {
      const tr = document.createElement("tr");
      tr.style.borderWidth = "0";

      const th = document.createElement("th");
      th.style.borderWidth = "0";
      th.colSpan = 4;
      const text = document.createTextNode(title);

      th.appendChild(text);
      tr.appendChild(th);
      tableHead.appendChild(tr);
    });

    const tableBody = document.createElement("tbody");
    const split_index = 4;
    let row_element: HTMLTableRowElement | null = null;

    bodyLines.forEach((body: any, i: any) => {
      const colors = tooltip.labelColors[i];

      const span = document.createElement("span");
      span.style.background = colors.backgroundColor;
      span.style.borderColor = colors.borderColor;
      span.style.borderWidth = "2px";
      span.style.marginRight = "10px";
      span.style.height = "10px";
      span.style.width = "10px";
      span.style.display = "inline-block";

      const td = document.createElement("td");
      td.style.borderWidth = "0";
      td.style.padding = "0 0.25rem";

      const [_renderText] = body;

      const [key, value] = _renderText.split(":");

      const formattedValue = retrieveTransformedValue(key.trim(), value.trim(), keysVirtualThreshold, STATIC_KEYS_INFO);

      const text = document.createTextNode(
        `${STATIC_KEYS_INFO?.[key]?.label ?? key} : ${formattedValue} ${STATIC_KEYS_INFO?.[key?.toUpperCase()]?.unit}`,
      );

      td.appendChild(span);
      td.appendChild(text);

      if ((i + 1) % split_index === 0 && row_element) {
        row_element.appendChild(td);
        tableBody.appendChild(row_element);
        row_element = null;
      } else {
        if (!row_element) {
          row_element = document.createElement("tr");
          row_element.style.backgroundColor = "inherit";
          row_element.style.borderWidth = "0";
        }
        row_element.appendChild(td);
      }
    });

    const tableRoot = tooltipEl.querySelector("table");

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const position_from_top = tooltip.caretY + positionY;
  const position_from_left = tooltip.caretX + positionX;
  const tooltipHeight = tooltipEl.offsetHeight;
  const tooltipWidth = tooltipEl.offsetWidth;

  const tooltip_y_offset = 20;
  const tooltip_x_offset = 200;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px";

  if (tooltipHeight + position_from_top > viewportHeight) {
    tooltipEl.style.bottom = viewportHeight - position_from_top + tooltip_y_offset + "px";
    tooltipEl.style.top = "inherit";
  } else {
    tooltipEl.style.top = position_from_top + tooltip_y_offset + "px";
    tooltipEl.style.bottom = "inherit";
  }

  if (tooltipWidth + position_from_left + tooltip_x_offset > viewportWidth) {
    tooltipEl.style.right = viewportWidth - position_from_left - tooltip_x_offset + "px";
    tooltipEl.style.left = "inherit";
  } else {
    tooltipEl.style.left = position_from_left + tooltip_x_offset + "px";
    tooltipEl.style.right = "inherit";
  }
};

const BhmsHistoryGraph: React.FC<any> = ({ loading, graphState, columns, deviceId, liveData }) => {
  const graph_raw_data = graphState?.data?.data?.data?.content ?? [];
  console.log("graph_raw_data", graph_raw_data);

  return (
    <div style={{ height: "100%" }}>
      <PointHistoryGraph historyData={graphState?.data?.data?.data?.content ?? []} isLoading={loading} />
    </div>
  );
};

const initChartData = {
  labels: [],
  datasets: [
    {
      label: "No Data Available",
      data: [],
      borderColor: "rgba(75,192,192,1)",
      borderWidth: 2,
      fill: false,
    },
  ],
};
const PointHistoryGraphComponent: React.FC<any> = ({
  transformedApiHistoryData,
  keysVirtualThreshold,
  spanGaps,
  config,
}) => {
  const { STATIC_KEYS_INFO, TOTAL_POSSIBLE_PM_KEYS } = config;
  type StaticKeyType = keyof typeof STATIC_KEYS_INFO;
  const [pointChartData, setPointChartData] = useImmer(initChartData);

  const pointChartAnnotations = getAnnotaionsForPointGraph(
    keysVirtualThreshold,
    TOTAL_POSSIBLE_PM_KEYS,
    STATIC_KEYS_INFO,
  );

  useEffect(() => {
    const keyDataObj: any = {};
    let pointChartLabelTimeStamps: any = [];
    Object.entries(transformedApiHistoryData).forEach(([ts, keyValPacket]: any) => {
      pointChartLabelTimeStamps.push(new Date(Number(ts)).toLocaleString("en-GB"));
      function pushDataForKey(key: string) {
        if (keyDataObj[key]) {
          keyDataObj[key].push(keyValPacket[key]);
        } else {
          keyDataObj[key] = [keyValPacket[key]];
        }
      }
      TOTAL_POSSIBLE_PM_KEYS.forEach((key: any) => {
        if (Array.isArray(key)) {
          for (const _key of key) {
            pushDataForKey(_key);
          }
        } else {
          pushDataForKey(key);
        }
      });
    });

    function getChartDataSet(key: StaticKeyType) {
      return {
        label: STATIC_KEYS_INFO?.[key]?.label ?? key,
        data: keyDataObj[key],
        dataKey: key,
        borderColor: STATIC_KEYS_INFO?.[key]?.lineColor ?? "grey",
        backgroundColor: STATIC_KEYS_INFO?.[key]?.lineColor ?? "grey",
        borderWidth: 1.3,
        fill: false,
        pointRadius: 1.5,
        pointHoverRadius: 1.5,
        spanGaps: spanGaps,
      };
    }
    const pointChartDataSet: any[] = [];

    for (const key of TOTAL_POSSIBLE_PM_KEYS) {
      if (Array.isArray(key)) {
        for (const _key of key) {
          pointChartDataSet.push(getChartDataSet(_key));
        }
      } else {
        pointChartDataSet.push(getChartDataSet(key));
      }
    }

    setPointChartData((prev) => {
      prev.labels = pointChartLabelTimeStamps;
      prev.datasets = pointChartDataSet;
    });
  }, [transformedApiHistoryData]);

  const pointChartOption: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
      callbacks: {
        intersect: function () {},
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        type: "linear",
        position: "left",
        max: getMaxValueOfSgYaxis(TOTAL_POSSIBLE_PM_KEYS),
        min: 0,
        scaleOverride: true,
        grid: {
          display: false,
        },
        ticks: {
          stepSize: 2,
          font: {
            size: 10,
          },
          autoSkip: false,
          callback: (value: any) => {
            let tickLabel = "";
            Object.entries(keysVirtualThreshold).forEach(([key, virtualThresholds]: any) => {
              const startTickVal = virtualThresholds.virtualMin;
              const endTick = virtualThresholds.virtualMax;
              const tickValForKey = (startTickVal + endTick) / 2;
              const tickUnit = STATIC_KEYS_INFO?.[key]?.unit ?? "";
              const maxtickValueToBeShownOnGraph = STATIC_KEYS_INFO?.[key]?.actualUnitsForYaxis ?? "";
              if (startTickVal === value) {
                tickLabel = `${0} ${tickUnit}`;
              }
              if (endTick === value) {
                tickLabel = `${maxtickValueToBeShownOnGraph} ${tickUnit}`;
              }
              if (STATIC_KEYS_INFO?.[key]?.custom_range) {
                if (startTickVal === value) {
                  tickLabel = `${STATIC_KEYS_INFO?.[key]?.custom_range.min} ${tickUnit}`;
                }
                if (endTick === value) {
                  tickLabel = `${STATIC_KEYS_INFO?.[key]?.custom_range.max} ${tickUnit}`;
                }
              }
              if (tickValForKey === value) {
                tickLabel = STATIC_KEYS_INFO?.[key]?.box_label ?? STATIC_KEYS_INFO?.[key]?.label ?? key;
              }
            });

            return tickLabel;
          },
        },
      },
    },
    plugins: {
      title: {
        display: false,
        text: "Chart.js Bar Chart - Stacked",
        align: "right",
      },
      legend: {
        display: true,
      },
      tooltip: {
        enabled: false,
        position: "nearest",
        external: (context: any) => externalTooltipHandler(context, keysVirtualThreshold, STATIC_KEYS_INFO),
      },
      datalabels: {
        formatter: function () {
          return "";
        },
      },
      annotation: {
        annotations: pointChartAnnotations,
      },
    },
  };
  return <Line data={pointChartData} options={pointChartOption} />;
};

const PointHistoryGraph: React.FC<any> = ({ historyData, isLoading, columns }) => {
  let location = useLocation();
  if (isLoading === true || isLoading === undefined) {
    return <Loader />;
  } else {
    const deviceName = location.pathname.split("/")[2].split("_")[2];
    const stationName = deviceName.split("-")?.[0];
    const config_type = BHMS_STATION_CONFIG_MAP[stationName as keyof typeof BHMS_STATION_CONFIG_MAP];
    const config = getBHMSHistoryConfig(config_type);
    const { STATIC_KEYS_INFO = {}, TOTAL_POSSIBLE_PM_KEYS = [] } = config ?? {};
    const { transformHistoryData, keysVirtualThreshold } = getTransformedSgHistorydata(
      historyData,
      TOTAL_POSSIBLE_PM_KEYS,
      STATIC_KEYS_INFO,
    );
    return (
      <div className="historyChartViewComponentContainer" style={{ height: "100%" }}>
        <PointHistoryGraphComponent
          transformedApiHistoryData={transformHistoryData}
          keysVirtualThreshold={keysVirtualThreshold}
          spanGaps={true}
          config={config}
        />
      </div>
    );
  }
};

export default BhmsHistoryGraph;
